import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { Backend } from "../url";

let accessToken: string | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = (): string | null => accessToken;

export const client = axios.create({
  baseURL: Backend,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // Prevent browser caching for GET requests
  if (config.method?.toUpperCase() === 'GET') {
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    config.params = { ...config.params, _t: Date.now() };
  }

  return config;
});

let refreshPromise: Promise<string | null> | null = null;

const requestRefresh = async (): Promise<string | null> => {
  try {
    const resp = await axios.post(
      `${Backend}/auth/refresh/`,
      {},
      { withCredentials: true }
    );
    const newAccess: string | undefined = resp.data?.access;
    if (newAccess) {
      setAccessToken(newAccess);
      return newAccess;
    }
    return null;
  } catch {
    setAccessToken(null);
    return null;
  }
};

export const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = requestRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

type RetriableConfig = AxiosRequestConfig & { _retry?: boolean };

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = original?.url ?? "";

    // Don't try to refresh for the auth endpoints themselves.
    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const newAccess = await refreshAccessToken();
      if (newAccess) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization =
          `Bearer ${newAccess}`;
        return client.request(original);
      }
    }
    return Promise.reject(error);
  }
);
